import React, { useState, useEffect } from 'react';
import Button from 'antd/lib/button';
import Overlay from '../Overlay';
import { API, graphqlOperation } from 'aws-amplify';
import { useSelector, useDispatch } from 'react-redux';
import message from 'antd/lib/message';

const getTermsQuery = `
  query GetTermsOfUse($pk: ID!) {
    getTermsOfUse(pk: $pk, sk: "TERMS#LATEST") {
      text
      version
      enabled
      requireReacceptance
    }
  }
`;

const updateOccupantQuery = `
  mutation UpdateOccupant($pk: ID!, $sk: String!, $expectedVersion: Int!, $input: updateOccupantInput!) {
    updateOccupant(pk: $pk, sk: $sk, expectedVersion: $expectedVersion, input: $input) {
      version
      extraFields {
        key
        value
      }
    }
  }
`;

const styles = {
    container: {
        padding: '1em',
        width: 500,
        maxHeight: '70vh',
        overflow: 'auto'
    },
    content: {
        whiteSpace: 'pre-wrap',
        marginBottom: '15px',
        minHeight: 380,
        backgroundColor: '#fff',
        padding: '15px',
        border: '1px solid #d9d9d9',
        borderRadius: '4px'
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'center',
        marginTop: '15px'
    },
    headerText: {
        textAlign: 'center',
        width: '100%',
        fontSize: '24px',
        fontWeight: 600,
        marginBottom: '15px',
        fontFamily: 'Avenir, sans-serif'
    }
};

const TermsOfUseAcceptance = () => {
    const dispatch = useDispatch();
    
    const [termsText, setTermsText] = useState('');
    const [visible, setVisible] = useState(false);
    const [loading, setLoading] = useState(true);

    const user = useSelector(state => state.app.user);
    const buildingId = useSelector(state => state.app.building?.pk);
    const buildingSettings = useSelector(state => state.app.building?.settings);
    const isSuperAdmin = useSelector(state => state.app.user?.roles?.includes('SUPER_ADMIN'));

    useEffect(() => {
        const checkTermsAcceptance = async () => {
            if (!buildingId || !user) {
                setLoading(false);
                return;
            }

            // First check if terms of use is enabled in building settings
            const termsEnabled = buildingSettings?.systemConfig?.termsOfUse;
            if (!termsEnabled) {
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                const response = await API.graphql(graphqlOperation(getTermsQuery, {
                    pk: buildingId
                }));

                const terms = response?.data?.getTermsOfUse;
                if (!terms || !terms.enabled) {
                    setLoading(false);
                    return;
                }

                // Validate terms version
                if (terms.version < 1) {
                    console.error('[Terms Check] Invalid terms version:', terms.version);
                    setLoading(false);
                    return;
                }

                setTermsText(terms.text || '');

                // Get the user's accepted version from extraFields for this specific building
                const acceptedVersionField = user.extraFields?.find(field => field.key === `acceptedTermsVersion_${buildingId}`);
                const acceptedVersion = acceptedVersionField ? parseInt(acceptedVersionField.value, 10) : 0;

                // Validate accepted version
                if (acceptedVersion < 0) {
                    console.error('[Terms Check] Invalid accepted version:', acceptedVersion);
                    setLoading(false);
                    return;
                }

                // Show modal if:
                // 1. User hasn't accepted any version for this building
                // 2. Terms version is higher than accepted version AND requireReacceptance is true
                const shouldShowTerms = !acceptedVersion || (terms.version > acceptedVersion && terms.requireReacceptance);

                // Log version check for debugging
                console.log('[Terms Check] Version comparison:', {
                    currentTermsVersion: terms.version,
                    userAcceptedVersion: acceptedVersion,
                    requireReacceptance: terms.requireReacceptance,
                    shouldShowTerms,
                    enabled: terms.enabled,
                    buildingId,
                    termsEnabled,
                    acceptedVersionKey: `acceptedTermsVersion_${buildingId}`
                });

                setVisible(shouldShowTerms);
            } catch (error) {
                console.error('[Terms Check] Error checking terms:', error);
                message.error('Error checking terms of use status');
            } finally {
                setLoading(false);
            }
        };

        // Only check terms when we have both user and building context
        if (buildingId && user) {
            checkTermsAcceptance();
        }
    }, [buildingId, user, buildingSettings]);

    const handleAccept = async () => {
        if (!buildingId || !user) {
            message.error('Missing required context for accepting terms');
            return;
        }

        setLoading(true);
        try {
            // Get current terms version
            const termsResponse = await API.graphql(graphqlOperation(getTermsQuery, {
                pk: buildingId
            }));

            const terms = termsResponse?.data?.getTermsOfUse;
            if (!terms) {
                throw new Error('Could not get current terms version');
            }

            // Add validation for terms version
            if (terms.version < 1) {
                console.error('[Terms Error] Invalid terms version:', terms.version);
                message.error('Invalid terms version. Please contact support.');
                return;
            }

            // Get current accepted version
            const currentAcceptedVersionField = user.extraFields?.find(field => field.key === `acceptedTermsVersion_${buildingId}`);
            const currentAcceptedVersion = currentAcceptedVersionField ? parseInt(currentAcceptedVersionField.value, 10) : 0;

            // Validate that new version is greater than current accepted version
            if (terms.version <= currentAcceptedVersion) {
                console.error('[Terms Error] New version not greater than accepted version:', {
                    currentAcceptedVersion,
                    newVersion: terms.version
                });
                message.error('Invalid terms version. Please contact support.');
                return;
            }

            // Update extraFields with accepted version for this specific building
            const extraFields = [...(user.extraFields || [])];
            const versionIndex = extraFields.findIndex(field => field.key === `acceptedTermsVersion_${buildingId}`);

            if (versionIndex >= 0) {
                extraFields[versionIndex].value = terms.version.toString();
            } else {
                extraFields.push({
                    key: `acceptedTermsVersion_${buildingId}`,
                    value: terms.version.toString()
                });
            }

            // Log version update for debugging
            console.log('[Terms Update] Updating user accepted version:', {
                buildingId,
                oldVersion: currentAcceptedVersion,
                newVersion: terms.version,
                termsEnabled: terms.enabled,
                requireReacceptance: terms.requireReacceptance
            });

            const response = await API.graphql(graphqlOperation(updateOccupantQuery, {
                pk: user.pk,
                sk: user.sk,
                expectedVersion: user.version,
                input: {
                    extraFields
                }
            }));

            if (response?.data?.updateOccupant) {
                dispatch({
                    type: 'UPDATE_USER',
                    payload: {
                        ...user,
                        extraFields: response.data.updateOccupant.extraFields || extraFields,
                        version: response.data.updateOccupant.version
                    }
                });
                setVisible(false);
                message.success('Terms of Use accepted');
            }
        } catch (error) {
            console.error('[Terms Error] Error accepting terms:', error);
            message.error('Failed to accept Terms of Use. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (!visible || loading || !buildingId || !user) {
        return null;
    }

    return (
        <Overlay
            visible={visible}
            closebutton={false}
            headerText=""
            minimal={true}
            style={{ borderTop: 'none' }}
        >
            <div style={styles.container}>
                <div style={styles.headerText}>Terms of Use</div>
                <div style={styles.content}>
                    {loading ? 'Loading...' : termsText}
                </div>
                <div style={styles.buttonContainer}>
                    <Button
                        type="primary"
                        onClick={handleAccept}
                        size="large"
                        disabled={loading}
                    >
                        Accept
                    </Button>
                </div>
            </div>
        </Overlay>
    );
};

export default TermsOfUseAcceptance; 